/**
 * @userstory US-STRIPE-01, US-STRIPE-02, US-STRIPE-03, US-SUB-01, US-SUB-03, US-SUB-04, US-SUB-05
 *
 * API client for subscription management endpoints.
 * Provides methods for viewing subscriptions (US-STRIPE-01 / US-SUB-01),
 * generating Stripe Customer Portal URLs (US-STRIPE-02 / US-SUB-03),
 * and synchronizing subscription state with Stripe (US-STRIPE-03).
 */
import apiClient from './apiClient';

// ============================================================================
// Types
// ============================================================================

/**
 * Response from GET /api/organizations/{id}/subscriptions/portal
 * or GET /api/talents/{id}/subscriptions/portal
 */
export interface PortalResponse {
	url: string;
}

export interface PortalErrorResponse {
	code: string;
	messageKey: string;
}

/**
 * Response from GET /api/organizations/{id}/subscriptions/current
 * or GET /api/talents/{id}/subscriptions/current
 */
export interface CurrentSubscription {
	plan: string; // "Free" | "Team" (Company Licenza) | "Active" (Talent)
	status: string; // "Active" | "Canceled" | "PastDue"
	isPaid: boolean;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
	maxActiveQuotes: number;
	activeQuotesCount: number;
	remainingQuotes: number;
	canCreateQuote: boolean;
	usagePercentage: number;
}

export type SubscriptionDashboardState =
	| 'active'
	| 'available'
	| 'requires_paid'
	| 'deferred'
	| 'locked'
	| 'exhausted';

export const SubscriptionEntitlementCodes = {
	companyFree: 'COMPANY_FREE',
	companyPaid: 'COMPANY_PAID',
	talentFree: 'TALENT_FREE',
	talentPaid: 'TALENT_PAID',
	addOnCompanyFreelanceManagement: 'addon.freelance_management',
} as const;

export interface SubscriptionPackageSummary {
	plan: string;
	entitlementCode: string;
	labelKey: string;
	status: string;
	statusKey: string;
	isPaid: boolean;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
}

export interface SubscriptionRoleState {
	canManageSubscription: boolean;
	isReadOnly: boolean;
	reasonCode?: string | null;
}

export interface SubscriptionDashboardAction {
	code: string;
	labelKey: string;
	kind: string;
	isEnabled: boolean;
	disabledReasonCode?: string | null;
}

export interface SubscriptionUsageCard {
	code: string;
	labelKey: string;
	descriptionKey?: string | null;
	used?: number | null;
	limit?: number | null;
	remaining?: number | null;
	isUnlimited: boolean;
	isOverflow: boolean;
	state: SubscriptionDashboardState;
	requiredEntitlementCode?: string | null;
}

export interface SubscriptionAddOnCard {
	code: string;
	entitlementCode: string;
	labelKey: string;
	descriptionKey?: string | null;
	state: SubscriptionDashboardState;
	selfService: boolean;
	requiresEntitlementCode?: string | null;
	action: SubscriptionDashboardAction;
}

export interface SubscriptionDashboardWarning {
	code: string;
	severity: string;
	messageKey: string;
}

// ─── Pricing Catalog ────────────────────────────────────────────────────────

/** @userstory US-CSUB-09 */
export interface PlanCardCapability {
	key: string;
	included: boolean;
}

/** @userstory US-CSUB-09, US-CSUB-14 */
export interface PlanCardQuota {
	key: string;
	monthlyLimit: number | null;
	isUnlimited: boolean;
	/** 'per_seat' (pool scales with purchased licenses) | 'per_org' (single flat allowance). */
	scope: string;
}

/** @userstory US-CSUB-08 */
export interface PlanCardDto {
	planKey: string;
	/** Numeric amount sourced live from Stripe; null when unavailable. The frontend formats it. */
	priceAmount: number | null;
	isStartingPrice?: boolean;
	currency: string;
	/** Stripe recurring interval, e.g. 'month' | 'year'. */
	billingInterval: string;
	ctaKind: string;
	capabilities: PlanCardCapability[];
	quotas: PlanCardQuota[];
}

/** @userstory US-FM-03, US-FM-07, US-CSUB-10 */
export interface FreelanceManagementCardDto {
	isActive: boolean;
	priceAmount: number | null;
	yearlyPriceAmount: number | null;
	currency: string;
	ctaVariant: string; // 'add' | 'manage'
}

/** @userstory US-CSUB-09, US-SUBCAT-11 */
export interface SubscriptionCatalogDto {
	plans: PlanCardDto[];
	freelanceManagement: FreelanceManagementCardDto | null;
}

// ─── In-app billing ─────────────────────────────────────────────

/** @userstory US-CSUB-17 */
export interface PaymentMethodDto {
	id: string;
	brand: string;
	last4: string;
	expiryMonth: number;
	expiryYear: number;
}

/** @userstory US-CSUB-17 */
export interface SetupIntentDto {
	clientSecret: string;
	publishableKey: string;
}

/**
 * One discount on the subscription. Several can be active at once, and 100% off is an ordinary
 * case: a pilot customer pays nothing and is a subscriber like any other.
 * @userstory US-CSUB-27
 */
export interface StripeDiscount {
	id: string;
	/** The code the customer typed; null for a coupon attached directly in Stripe. */
	code?: string | null;
	name?: string | null;
	percentOff?: number | null;
	amountOff?: number | null;
	currency?: string | null;
	/** once | repeating | forever */
	duration: string;
	durationInMonths?: number | null;
	endsAt?: string | null;
}

/** @userstory US-CSUB-17, US-CSUB-25 */
export interface CompanyBillingState {
	paymentMethod: PaymentMethodDto | null;
	purchasedLicenses: number;
	assignedLicenses: number;
	availableLicenses: number;
	/** Monthly unit price, from Stripe. Monthly is the only cadence since 2026-09-08. */
	licensePrice: number | null;
	/** The discounts on the subscription right now. @userstory US-CSUB-27 */
	discounts: StripeDiscount[];
	currency: string;
	billingInterval: string;
	/** List price × licenses, VAT excluded, no discounts. */
	recurringAmount: number | null;
	/** What the next invoice will charge, VAT excluded, discounts included (Stripe). @userstory US-CSUB-27 */
	nextChargeAmount: number | null;
	/** `recurringAmount` after the recurring discounts. @userstory US-CSUB-27 */
	recurringAmountAfterDiscounts: number | null;
	/** VAT stated under the headline figure. @userstory US-CSUB-19 */
	vat: VatInfo | null;
	/** The headline figure with VAT included. @userstory US-CSUB-19 */
	headlineAmountIncludingVat: number | null;
	nextBillingDate: string | null;
	isFreeTrial: boolean;
	isSuspended: boolean;
	cancelAtPeriodEnd: boolean;
	canManageBilling: boolean;
	/**
	 * AI credits one licensed seat carries, whatever plan the organization is on today.
	 * Drives the "what changes" projection, which is about the plan being bought.
	 * @userstory US-CSUB-13, US-CSUB-19
	 */
	licensedCreditsPerLicense: number | null;
}

/** @userstory US-CSUB-19 */
export interface LicenseChangePreview {
	currentLicenses: number;
	requestedLicenses: number;
	amountDueNow: number;
	taxDueNow: number;
	recurringAmount: number;
	currency: string;
	nextBillingDate: string | null;
	isCredit: boolean;
	/** Renewal after the subscription's and the queued discounts. @userstory US-CSUB-27 */
	recurringAmountAfterDiscounts: number | null;
	/** True when the amounts are a local estimate because Stripe did not answer. */
	isEstimate: boolean;
	/** VAT included in `amountDueNow`. @userstory US-CSUB-19 */
	vat: VatInfo | null;
}

/**
 * The VAT a customer pays: Stripe's rate for this customer, reverse charge for an EU business with a
 * foreign VAT ID, or the configured default labelled as an estimate.
 * @userstory US-CSUB-19
 */
export interface VatInfo {
	ratePercent: number;
	isReverseCharge: boolean;
	isEstimate: boolean;
}

/** @userstory US-CSUB-19 */
export interface LicenseChangeRequest {
	licenses: number;
	/** Codes to apply on a first purchase. More than one is allowed. @userstory US-CSUB-27 */
	promotionCodes?: string[];
}

/** @userstory US-CSUB-22 */
export interface InvoiceDto {
	id: string;
	number: string | null;
	issuedAt: string | null;
	total: number;
	currency: string;
	status: string;
	hasPdf: boolean;
}

/** @userstory US-CSUB-22, US-TSUB-14 An invoice document fetched through the authenticated client. */
export interface InvoiceDocument {
	blob: Blob;
	fileName: string;
}

/** Reads the file name from the content-disposition header, falling back to the invoice id. */
const fileNameFromResponse = (
	headers: Record<string, unknown> | undefined,
	invoiceId: string,
): string => {
	const disposition = String(
		(headers?.['content-disposition'] as string | undefined) ??
			(headers?.['Content-Disposition'] as string | undefined) ??
			'',
	);
	const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
	return match?.[1] ? decodeURIComponent(match[1]) : `${invoiceId}.pdf`;
};

/** @userstory US-CSUB-18 */
export interface PromotionCodeValidation {
	isValid: boolean;
	code: string;
}

/** @userstory US-TSUB-01 */
export type TalentBillingState = 'passive' | 'active' | 'paused' | 'suspended';

/** @userstory US-TSUB-01 */
export interface TalentBilling {
	paymentMethod: PaymentMethodDto | null;
	state: TalentBillingState;
	monthlyPrice: number | null;
	yearlyPrice: number | null;
	currency: string;
	billingInterval: string;
	recurringAmount: number | null;
	nextBillingDate: string | null;
	activatedAt: string | null;
	activeMonthsThisYear: number;
	accessUntilPeriodEnd: boolean;
	billingProfileComplete: boolean;
}

/** @userstory US-TSUB-09 */
export interface ActivateTalentPlanRequest {
	billingInterval: 'month' | 'year';
	promotionCode?: string | null;
}

// ─── Current Usage ──────────────────────────────────────────────────────────

/** @userstory US-CSUB-12, US-CSUB-13 */
export interface QuotaUsageItem {
	key: string;
	used: number;
	monthlyLimit: number | null;
	isUnlimited: boolean;
	/** @userstory US-CSUB-13 True when the limit is a per-license pool (perLicenseBase × purchasedLicenses). */
	isPooled?: boolean;
	/** @userstory US-CSUB-13 Per-license base allotment for a pooled quota; null otherwise. */
	perLicenseBase?: number | null;
	/** @userstory US-CSUB-13 Number of purchased licenses the pool is multiplied by (>= 1). */
	purchasedLicenses?: number;
}

/** @userstory US-CSUB-12 */
export interface CurrentUsage {
	quotas: QuotaUsageItem[];
	/** @userstory US-COM-FG-25 True when the organization is on the premium AI model tier. */
	premiumAiModel?: boolean;
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface SubscriptionDashboard {
	audience: 'company' | 'talent';
	package: SubscriptionPackageSummary;
	roleState: SubscriptionRoleState;
	primaryAction: SubscriptionDashboardAction;
	usageCards: SubscriptionUsageCard[];
	addOns: SubscriptionAddOnCard[];
	warnings: SubscriptionDashboardWarning[];
	/** Pricing catalog — present only for company dashboard */
	catalog?: SubscriptionCatalogDto | null;
	/** Current-period usage for the plan's quotas — present only for company dashboard */
	currentUsage?: CurrentUsage | null;
}

/**
 * Append-only history item.
 * @userstory US-CSUB-01, US-TSUB-01
 */
export interface SubscriptionHistoryItem {
	id: string;
	plan: string;
	status: string;
	periodStart: string;
	periodEnd: string;
	createdAt: string;
}

// ============================================================================
// Service
// ============================================================================

const subscriptionService = {
	/**
	 * Gets the Stripe Customer Portal URL for an organization.
	 * @param organizationId - The organization ID
	 * @param returnUrl - URL to redirect back to after portal session
	 * @returns The Stripe portal URL
	 */
	getOrganizationPortalUrl: async (
		organizationId: string,
		returnUrl?: string,
	): Promise<string> => {
		const params = new URLSearchParams();
		if (returnUrl) {
			params.append('returnUrl', returnUrl);
		}
		const queryString = params.toString();
		const url = `/api/organizations/${organizationId}/subscriptions/portal${queryString ? `?${queryString}` : ''}`;

		const response = await apiClient.get<PortalResponse>(url);
		return response.data.url;
	},

	/**
	 * Gets the Stripe Customer Portal URL for a talent.
	 * @param talentId - The talent ID
	 * @param returnUrl - URL to redirect back to after portal session
	 * @returns The Stripe portal URL
	 */
	getTalentPortalUrl: async (talentId: string, returnUrl?: string): Promise<string> => {
		const params = new URLSearchParams();
		if (returnUrl) {
			params.append('returnUrl', returnUrl);
		}
		const queryString = params.toString();
		const url = `/api/talentusers/me/subscriptions/portal${queryString ? `?${queryString}` : ''}`;

		const response = await apiClient.get<PortalResponse>(url);
		return response.data.url;
	},

	/**
	 * Gets current subscription details for an organization.
	 * @param organizationId - The organization ID
	 * @returns Current subscription data
	 */
	getOrganizationSubscription: async (organizationId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.get<CurrentSubscription>(
			`/api/organizations/${organizationId}/subscriptions/current`,
		);
		return response.data;
	},

	/** @userstory US-CSUB-01, US-CSUB-02, US-CSUB-04, US-CSUB-06 */
	getOrganizationSubscriptionDashboard: async (
		organizationId: string,
	): Promise<SubscriptionDashboard> => {
		const response = await apiClient.get<SubscriptionDashboard>(
			`/api/organizations/${organizationId}/subscriptions/dashboard`,
		);
		return response.data;
	},

	/** @userstory US-CSUB-17, US-CSUB-25 */
	getOrganizationBillingState: async (organizationId: string): Promise<CompanyBillingState> => {
		const response = await apiClient.get<CompanyBillingState>(
			`/api/organizations/${organizationId}/subscriptions/billing`,
		);
		return response.data;
	},

	/** @userstory US-CSUB-17 */
	createOrganizationSetupIntent: async (organizationId: string): Promise<SetupIntentDto> => {
		const response = await apiClient.post<SetupIntentDto>(
			`/api/organizations/${organizationId}/subscriptions/payment-method/setup-intent`,
		);
		return response.data;
	},

	/** @userstory US-CSUB-17 */
	setOrganizationPaymentMethod: async (
		organizationId: string,
		paymentMethodId: string,
	): Promise<PaymentMethodDto> => {
		const response = await apiClient.put<PaymentMethodDto>(
			`/api/organizations/${organizationId}/subscriptions/payment-method`,
			{ paymentMethodId },
		);
		return response.data;
	},

	/** @userstory US-CSUB-19 */
	previewOrganizationLicenseChange: async (
		organizationId: string,
		request: LicenseChangeRequest,
	): Promise<LicenseChangePreview> => {
		const response = await apiClient.post<LicenseChangePreview>(
			`/api/organizations/${organizationId}/subscriptions/licenses/preview`,
			request,
		);
		return response.data;
	},

	/** @userstory US-CSUB-18, US-CSUB-19 */
	changeOrganizationLicenses: async (
		organizationId: string,
		request: LicenseChangeRequest,
	): Promise<CompanyBillingState> => {
		const response = await apiClient.put<CompanyBillingState>(
			`/api/organizations/${organizationId}/subscriptions/licenses`,
			request,
		);
		return response.data;
	},

	/** @userstory US-CSUB-22 */
	getOrganizationInvoices: async (organizationId: string): Promise<InvoiceDto[]> => {
		const response = await apiClient.get<InvoiceDto[]>(
			`/api/organizations/${organizationId}/subscriptions/invoices`,
		);
		return response.data;
	},

	/**
	 * @userstory US-CSUB-22
	 * Downloads the invoice document through the authenticated client. A bare link would resolve
	 * against the web origin and travel without the bearer token, so the file is fetched here.
	 */
	downloadOrganizationInvoicePdf: async (
		organizationId: string,
		invoiceId: string,
	): Promise<InvoiceDocument> => {
		const response = await apiClient.get<Blob>(
			`/api/organizations/${organizationId}/subscriptions/invoices/${invoiceId}/pdf`,
			{ responseType: 'blob' },
		);
		return {
			blob: response.data,
			fileName: fileNameFromResponse(response.headers, invoiceId),
		};
	},

	/** @userstory US-CSUB-18 */
	validateOrganizationPromotionCode: async (
		organizationId: string,
		code: string,
	): Promise<PromotionCodeValidation> => {
		const response = await apiClient.post<PromotionCodeValidation>(
			`/api/organizations/${organizationId}/subscriptions/promotion-code/validate`,
			{ code },
		);
		return response.data;
	},

	/** @userstory US-CSUB-24 */
	setOrganizationCancelAtPeriodEnd: async (
		organizationId: string,
		cancelAtPeriodEnd: boolean,
	): Promise<CompanyBillingState> => {
		const response = await apiClient.post<CompanyBillingState>(
			`/api/organizations/${organizationId}/subscriptions/cancel`,
			{ cancelAtPeriodEnd },
		);
		return response.data;
	},

	/**
	 * Syncs the organization's subscription with Stripe.
	 * Creates a Free subscription if none exists.
	 * Should be called before accessing subscription data or portal.
	 * @param organizationId - The organization ID
	 * @returns Updated subscription data
	 */
	syncOrganizationSubscription: async (organizationId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.post<CurrentSubscription>(
			`/api/organizations/${organizationId}/subscriptions/sync`,
		);
		return response.data;
	},

	/**
	 * Gets current subscription details for a talent.
	 * @param talentId - The talent ID
	 * @returns Current subscription data
	 */
	getTalentSubscription: async (_talentId: string): Promise<CurrentSubscription> => {
		void _talentId;
		const response = await apiClient.get<CurrentSubscription>(
			`/api/talentusers/me/subscriptions/current`,
		);
		return response.data;
	},

	/** @userstory US-TSUB-01, US-TSUB-03, US-TSUB-09 */
	getTalentSubscriptionDashboard: async (): Promise<SubscriptionDashboard> => {
		const response = await apiClient.get<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/dashboard`,
		);
		return response.data;
	},

	/**
	 * Syncs the talent's subscription with Stripe.
	 * Creates a Free subscription if none exists.
	 * Should be called before accessing subscription data or portal.
	 * @param talentId - The talent ID
	 * @returns Updated subscription data
	 */
	syncTalentSubscription: async (_talentId: string): Promise<CurrentSubscription> => {
		void _talentId;
		const response = await apiClient.post<CurrentSubscription>(
			`/api/talentusers/me/subscriptions/sync`,
		);
		return response.data;
	},

	/** @userstory US-TSUB-01 */
	getTalentBillingState: async (): Promise<TalentBilling> => {
		const response = await apiClient.get<TalentBilling>(
			`/api/talentusers/me/subscriptions/billing`,
		);
		return response.data;
	},

	/** @userstory US-TSUB-09 */
	createTalentSetupIntent: async (): Promise<SetupIntentDto> => {
		const response = await apiClient.post<SetupIntentDto>(
			`/api/talentusers/me/subscriptions/payment-method/setup-intent`,
		);
		return response.data;
	},

	/** @userstory US-TSUB-09 */
	setTalentPaymentMethod: async (paymentMethodId: string): Promise<PaymentMethodDto> => {
		const response = await apiClient.put<PaymentMethodDto>(
			`/api/talentusers/me/subscriptions/payment-method`,
			{ paymentMethodId },
		);
		return response.data;
	},

	/** @userstory US-TSUB-14 */
	getTalentInvoices: async (): Promise<InvoiceDto[]> => {
		const response = await apiClient.get<InvoiceDto[]>(
			`/api/talentusers/me/subscriptions/invoices`,
		);
		return response.data;
	},

	/** @userstory US-TSUB-14 Downloads the document through the authenticated client, never a bare link. */
	downloadTalentInvoicePdf: async (invoiceId: string): Promise<InvoiceDocument> => {
		const response = await apiClient.get<Blob>(
			`/api/talentusers/me/subscriptions/invoices/${invoiceId}/pdf`,
			{ responseType: 'blob' },
		);
		return {
			blob: response.data,
			fileName: fileNameFromResponse(response.headers, invoiceId),
		};
	},

	/** @userstory US-TSUB-09 */
	validateTalentPromotionCode: async (code: string): Promise<PromotionCodeValidation> => {
		const response = await apiClient.post<PromotionCodeValidation>(
			`/api/talentusers/me/subscriptions/promotion-code/validate`,
			{ code },
		);
		return response.data;
	},

	/**
	 * Redirects user to Stripe Customer Portal for an organization.
	 * This performs a full page redirect.
	 * @param organizationId - The organization ID
	 * @param returnUrl - URL to redirect back to (defaults to current page)
	 */
	redirectToOrganizationPortal: async (
		organizationId: string,
		returnUrl?: string,
	): Promise<void> => {
		const url = await subscriptionService.getOrganizationPortalUrl(
			organizationId,
			returnUrl ?? window.location.href,
		);
		window.location.href = url;
	},

	/**
	 * Redirects talent to Stripe Customer Portal.
	 * @param talentId - The talent ID
	 * @param returnUrl - URL to redirect back to (defaults to current page)
	 */
	redirectToTalentPortal: async (talentId: string, returnUrl?: string): Promise<void> => {
		const url = await subscriptionService.getTalentPortalUrl(
			talentId,
			returnUrl ?? window.location.href,
		);
		window.location.href = url;
	},

	/**
	 * Activates the talent's episodic Active plan (or resumes a paused one).
	 * @userstory US-TSUB-09
	 * @returns Updated subscription dashboard
	 */
	activateTalentSubscription: async (
		request?: ActivateTalentPlanRequest,
	): Promise<SubscriptionDashboard> => {
		const response = await apiClient.post<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/activate`,
			request ?? { billingInterval: 'month' },
		);
		return response.data;
	},

	/**
	 * Pauses the talent's Active episodic plan.
	 * @userstory US-TSUB-09
	 * @returns Updated subscription dashboard
	 */
	pauseTalentSubscription: async (): Promise<SubscriptionDashboard> => {
		const response = await apiClient.post<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/pause`,
		);
		return response.data;
	},

	/**
	 * Resumes the talent's paused Active episodic plan.
	 * @userstory US-TSUB-09
	 * @returns Updated subscription dashboard
	 */
	resumeTalentSubscription: async (): Promise<SubscriptionDashboard> => {
		const response = await apiClient.post<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/resume`,
		);
		return response.data;
	},

	/**
	 * Gets the append-only subscription history for an organization.
	 * Used by the subscription page history drawer (lazy-loaded on open).
	 * @userstory US-CSUB-01
	 */
	getOrganizationSubscriptionHistory: async (
		organizationId: string,
	): Promise<SubscriptionHistoryItem[]> => {
		const response = await apiClient.get<SubscriptionHistoryItem[]>(
			`/api/organizations/${organizationId}/subscriptions/history`,
		);
		return response.data;
	},

	/**
	 * Gets the append-only subscription history for the current talent.
	 * @userstory US-TSUB-01
	 */
	getTalentSubscriptionHistory: async (): Promise<SubscriptionHistoryItem[]> => {
		const response = await apiClient.get<SubscriptionHistoryItem[]>(
			`/api/talentusers/me/subscriptions/history`,
		);
		return response.data;
	},
};

export default subscriptionService;
